import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { getYearOptions, exportToCsv } from '../../utils/registryExport'
import PatronIdentityCell from '../compliance-safe/components/PatronIdentityCell'
import { resolvePatronIdentity, clubOrVisitorIdLine } from '../../utils/patronIdentity'
import OcrExtractPanel from '../../components/OcrExtractPanel'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'

const KYC_REGISTRY_HELP_SCREEN_KEY = 'page:compliance-safe/kyc-registry'

// Image Preview Component
const ImagePreview = ({ kycId, filename, previewKey, imagePreviews, loadImagePreview }) => {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(false)

  React.useEffect(() => {
    const loadPreview = async () => {
      if (!imagePreviews[previewKey] && !loading && !error) {
        console.log(`Loading preview for ${filename}, key: ${previewKey}`)
        setLoading(true)
        const url = await loadImagePreview(kycId, filename)
        console.log(`Preview loaded for ${filename}, URL: ${url}`)
        setLoading(false)
        if (!url) {
          console.log(`Failed to load preview for ${filename}`)
          setError(true)
        }
      }
    }
    loadPreview()
  }, [kycId, filename, previewKey, imagePreviews, loadImagePreview, loading, error])

  if (loading) {
    return (
      <div className="w-full h-48 bg-gray-100 rounded flex items-center justify-center">
        <div className="text-xs text-gray-500">Loading preview...</div>
      </div>
    )
  }

  if (error || !imagePreviews[previewKey]) {
    console.log(`Preview unavailable for ${filename}. Error: ${error}, previewKey: ${previewKey}, has preview: ${!!imagePreviews[previewKey]}`)
    console.log('All imagePreviews:', Object.keys(imagePreviews))
    return (
      <div className="w-full h-48 bg-gray-100 rounded flex items-center justify-center">
        <div className="text-xs text-gray-500">Preview unavailable</div>
      </div>
    )
  }

  return (
    <div className="w-full h-48 overflow-y-auto border rounded bg-gray-50">
      <img 
        src={imagePreviews[previewKey]} 
        alt={filename}
        className="w-full h-auto object-contain"
        onError={(e) => {
          console.log(`Image load error for ${filename}:`, e)
          console.log(`Image src was: ${imagePreviews[previewKey]}`)
          setError(true)
        }}
        onLoad={() => {
          console.log(`Image loaded successfully for ${filename}`)
        }}
        title="Scroll vertically if image is tall"
      />
    </div>
  )
}

export default function KycRegistry() {
  const [searchParams, setSearchParams] = useSearchParams()
  const yearFromUrl = searchParams.get('year') || ''
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [filters, setFilters] = React.useState({ member_id:'', reason:'', search:'', venue_id:'', year: yearFromUrl || '' })
  const [venues, setVenues] = React.useState([])
  const [open, setOpen] = React.useState(false)
  const [helpPanelOpen, setHelpPanelOpen] = React.useState(false)
  const [form, setForm] = React.useState({ member_id:'', member_name:'', reason:'PAYOUT', venue_id:'', related_case_id:'', notes:'', payout_amount:'' })
  const [attachFor, setAttachFor] = React.useState(null)
  const [uploadFiles, setUploadFiles] = React.useState([])
  const [existingUploadFiles, setExistingUploadFiles] = React.useState([])
  const [imagePreviews, setImagePreviews] = React.useState({})
  const fileRef = React.useRef(null)
  const newEntryFileRef = React.useRef(null)

  const refreshAttachmentsForRow = React.useCallback(async (kycId) => {
    if (kycId == null) return
    try {
      const attachRes = await authFetch(getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments`))
      if (!attachRes.ok) return
      const attachData = await attachRes.json()
      if (!attachData.success || !Array.isArray(attachData.data)) return
      const names = attachData.data.map((att) => att.file_name).filter(Boolean)
      setItems((prev) =>
        prev.map((item) => (item.id === kycId ? { ...item, attachments: names } : item))
      )
    } catch (_) {}
  }, [])

  const openAttachments = React.useCallback(
    (kycId) => {
      setAttachFor(kycId)
      refreshAttachmentsForRow(kycId)
    },
    [refreshAttachmentsForRow]
  )

  const load = React.useCallback(async()=>{
    try {
      setLoading(true); setError(null)
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)))
      qs.set('limit', '500')
      const r = await authFetch(getLocalApiUrl(`/api/kyc-registry?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load KYC registry')
      const apiData = (j.data || []).map((item) => ({
        ...item,
        attachments: Array.isArray(item.attachments)
          ? item.attachments
          : Array.isArray(item.attachment_file_names)
            ? item.attachment_file_names
            : [],
        payout_amount: item.payout_amount || null,
        notes: item.notes || '',
        member_name: item.member_name || '',
        related_case_id: item.related_case_id || null,
      }))

      setItems(apiData)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [filters])

  React.useEffect(() => {
    if (yearFromUrl && yearFromUrl !== filters.year) {
      setFilters(prev => ({ ...prev, year: yearFromUrl }))
    }
  }, [yearFromUrl])

  const setYear = (y) => {
    setFilters(prev => ({ ...prev, year: y }))
    const next = new URLSearchParams(searchParams)
    if (y) next.set('year', y)
    else next.delete('year')
    setSearchParams(next, { replace: true })
  }

  React.useEffect(()=>{ load() }, [load])

  const handleExport = () => {
    const cols = [
      {
        header: 'Patron ID',
        accessor: (r) => {
          const id = resolvePatronIdentity(r)
          return id.patron_id || ''
        },
      },
      {
        header: 'Master Profile ID',
        accessor: (r) => resolvePatronIdentity(r).master_profile_id || '',
      },
      {
        header: 'Subject',
        accessor: (r) => resolvePatronIdentity(r).subject_type || '',
      },
      {
        header: 'Club / Visitor ID',
        accessor: (r) => {
          const sec = clubOrVisitorIdLine(resolvePatronIdentity(r))
          return sec ? `${sec.label}: ${sec.value}` : ''
        },
      },
      { header: 'Patron Name', accessor: r => r.member_name || '' },
      { header: 'Reason', accessor: r => r.reason || '' },
      { header: 'Venue', accessor: r => (venues.find(v => String(v.id) === String(r.venue_id))?.name) || r.venue_id || '' },
      { header: 'Related Case', accessor: r => r.related_case_id || '' },
      { header: 'Performed', accessor: r => r.performed_at ? new Date(r.performed_at).toLocaleString() : '' },
      { header: 'Notes', accessor: r => r.notes || '' },
    ]
    const suffix = filters.year ? `-${filters.year}` : ''
    exportToCsv(items, cols, `kyc-registry${suffix}.csv`)
  }

  const loadVenues = React.useCallback(async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/venues'))
      const j = await r.json()
      if (j.success && Array.isArray(j.data)) setVenues(j.data || [])
    } catch (e) { /* venues optional */ }
  }, [])
  React.useEffect(() => { loadVenues() }, [loadVenues])

  const create = async()=>{
    try {
      setLoading(true); setError(null)
      
      // Ensure payout_amount is properly formatted
      const formData = {
        ...form,
        venue_id: form.venue_id || null,
        payout_amount: form.payout_amount ? parseFloat(form.payout_amount) : null
      }
      
      console.log('Creating KYC entry with data:', formData)
      
      const r = await authFetch(getLocalApiUrl('/api/kyc-registry'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(formData) })
      const j = await r.json()
      
      console.log('Create KYC response:', j)
      
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to create KYC entry')
      
      // Upload files if any were selected
      if (uploadFiles.length > 0 && j.data?.id) {
        console.log(`Uploading ${uploadFiles.length} files for new KYC entry ${j.data.id}`)
        for (const file of uploadFiles) {
          try {
            console.log(`Uploading file: ${file.name}`)
            const fd = new FormData()
            fd.append('file', file)
            const uploadResponse = await authFetch(getLocalApiUrl(`/api/kyc-registry/${j.data.id}/attachments`), { method:'POST', body: fd })
            
            if (!uploadResponse.ok) {
              if (uploadResponse.status === 404) {
                throw new Error('KYC attachment upload is not available (404).')
              }
              throw new Error(`HTTP ${uploadResponse.status}: ${uploadResponse.statusText}`)
            }
            
            const uploadResult = await uploadResponse.json()
            if (!uploadResult.success) {
              throw new Error(uploadResult.error || `Failed to upload ${file.name}`)
            }
            console.log(`Successfully uploaded: ${file.name}`)
          } catch (uploadError) {
            console.warn('Failed to upload file:', file.name, uploadError)
            throw uploadError
          }
        }
      }
      
      setOpen(false)
      setForm({ member_id:'', member_name:'', reason:'PAYOUT', venue_id:'', related_case_id:'', notes:'', payout_amount:'' })
      setUploadFiles([])
      if (newEntryFileRef.current) newEntryFileRef.current.value = ''
      await load()
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }

  const upload = async(id)=>{
    try {
      if (!existingUploadFiles.length) {
        setError('No files selected for upload')
        return
      }
      
      console.log(`Uploading ${existingUploadFiles.length} files for KYC entry ${id}`)
      
      // Upload all selected files
      let successCount = 0
      for (const file of existingUploadFiles) {
        try {
          console.log(`Uploading file: ${file.name}`)
      const fd = new FormData()
          fd.append('file', file)
      const r = await authFetch(getLocalApiUrl(`/api/kyc-registry/${id}/attachments`), { method:'POST', body: fd })
          
          console.log(`Upload response status: ${r.status}`)
          
          if (!r.ok) {
            if (r.status === 404) {
              throw new Error('KYC attachment upload is not available (404).')
            }
            throw new Error(`HTTP ${r.status}: ${r.statusText}`)
          }
          
      const j = await r.json()
          console.log('Upload response:', j)
          
          if (!j.success) throw new Error(j.error || `Failed to upload ${file.name}`)
          successCount++
        } catch (uploadError) {
          console.error('Failed to upload file:', file.name, uploadError)
          setError(`Failed to upload ${file.name}: ${uploadError.message}`)
          return // Stop on first error
        }
      }
      
      if (successCount > 0) {
        console.log(`Successfully uploaded ${successCount} files`)
        // Data will be refreshed by the load() call below
      }
      
      // Clear the file input and close modal
      if (fileRef.current) fileRef.current.value = ''
      setExistingUploadFiles([])
      setAttachFor(null)
      await load()
    } catch (e) { 
      console.error('Upload error:', e)
      setError(e.message) 
    }
  }

  const handleNewEntryFileChange = (e) => {
    const files = Array.from(e.target.files || [])
    setUploadFiles(files)
  }

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleExistingUploadFileChange = (e) => {
    const files = Array.from(e.target.files || [])
    setExistingUploadFiles(files)
  }

  const removeExistingUploadFile = (index) => {
    setExistingUploadFiles(prev => prev.filter((_, i) => i !== index))
    // Update the file input to reflect the change
    if (fileRef.current) {
      const dt = new DataTransfer()
      existingUploadFiles.forEach((file, i) => {
        if (i !== index) dt.items.add(file)
      })
      fileRef.current.files = dt.files
    }
  }

  const loadImagePreview = async (kycId, filename) => {
    const key = `${kycId}_${filename}`
    console.log(`loadImagePreview called for key: ${key}`)
    
    if (imagePreviews[key]) {
      console.log(`Preview already exists for ${key}: ${imagePreviews[key]}`)
      return imagePreviews[key] // Already loaded
    }
    
    try {
      const url = getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments/${encodeURIComponent(filename)}`)
      console.log(`Fetching image from: ${url}`)
      
      const response = await authFetch(url)
      console.log(`Response status: ${response.status}, ok: ${response.ok}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
      }
      
      const contentType = response.headers.get('content-type')
      console.log(`Content-Type: ${contentType}`)
      
      const blob = await response.blob()
      console.log(`Blob created: size=${blob.size}, type=${blob.type}`)
      
      const blobUrl = URL.createObjectURL(blob)
      console.log(`Blob URL created: ${blobUrl}`)
      
      setImagePreviews(prev => ({
        ...prev,
        [key]: blobUrl
      }))
      
      return blobUrl
    } catch (error) {
      console.error('Error loading image preview:', error)
      return null
    }
  }

  const viewAttachment = async (kycId, filename) => {
    try {
      console.log(`Viewing attachment: ${filename} for KYC ${kycId}`)
      const response = await authFetch(getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments/${encodeURIComponent(filename)}`))
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
      }
      
      // Get the content type from response headers
      const contentType = response.headers.get('content-type') || 'application/octet-stream'
      console.log('File content type:', contentType)
      
      // Get the blob and create a URL for viewing
      const blob = await response.blob()
      console.log('Blob created:', blob.size, 'bytes, type:', blob.type)
      
      // Create blob with explicit type if needed
      const typedBlob = new Blob([blob], { type: contentType })
      const url = URL.createObjectURL(typedBlob)
      
      console.log('Opening URL:', url)
      
      // Open in new window/tab
      const newWindow = window.open(url, '_blank')
      
      // Check if popup was blocked
      if (!newWindow) {
        // Fallback: create a download link
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
      
      // Clean up the URL after a longer delay to ensure it loads
      setTimeout(() => {
        URL.revokeObjectURL(url)
        console.log('Blob URL cleaned up')
      }, 5000)
    } catch (error) {
      console.error('Error viewing attachment:', error)
      setError(`Failed to view ${filename}: ${error.message}`)
    }
  }

  const deleteAttachment = async (kycId, filename) => {
    if (!confirm(`Are you sure you want to delete "${filename}"?`)) {
      return
    }
    
    try {
      console.log(`Deleting attachment: ${filename} for KYC ${kycId}`)
      const response = await authFetch(getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments/${encodeURIComponent(filename)}`), {
        method: 'DELETE'
      })
      
      if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.status} ${response.statusText}`)
      }
      
      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete file')
      }
      
      console.log(`Successfully deleted: ${filename}`)
      
      // Refresh the data to update the UI
      await load()
    } catch (error) {
      console.error('Error deleting attachment:', error)
      setError(`Failed to delete ${filename}: ${error.message}`)
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">KYC Registry</h1>
            <div className="text-xs text-gray-500">KYC performed due to payout or other triggers. 7-year retention for reporting.</div>
          </div>
          <div className="flex items-center gap-3">
            <HelpScreenButton
              screenKey={KYC_REGISTRY_HELP_SCREEN_KEY}
              onClick={() => setHelpPanelOpen(true)}
            />
            <span className="text-sm text-gray-500">{loading ? 'Loading…' : `${items.length} entries`}</span>
            <button type="button" onClick={handleExport} className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm" disabled={loading || items.length === 0}>
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Year</label>
            <select className="select-field w-full" value={filters.year} onChange={(e) => setYear(e.target.value)}>
              <option value="">All years</option>
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Member #</label>
            <input className="input-field w-full" value={filters.member_id} onChange={(e)=>setFilters(p=>({...p, member_id:e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Reason</label>
            <select className="select-field w-full" value={filters.reason} onChange={(e)=>setFilters(p=>({...p, reason:e.target.value}))}>
              <option value="">All</option>
              <option value="PAYOUT">Payout</option>
              <option value="THRESHOLD">Threshold</option>
              <option value="MANUAL">Manual</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Venue</label>
            <select className="select-field w-full" value={filters.venue_id} onChange={(e)=>setFilters(p=>({...p, venue_id:e.target.value}))}>
              <option value="">All Venues</option>
              {venues.map(v => (
                <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Search</label>
            <input className="input-field w-full col-span-2" placeholder="member name, case id, notes" value={filters.search} onChange={(e)=>setFilters(p=>({...p, search:e.target.value}))} />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button className="btn-primary" onClick={()=>setOpen(true)}>New KYC Entry</button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Performed</th>
              <th className="px-3 py-2 text-left">Patron</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Amount</th>
              <th className="px-3 py-2 text-left">AML/PEP</th>
              <th className="px-3 py-2 text-left">Notes</th>
              <th className="px-3 py-2 text-left">Attachments</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {items.map(row => (
              <tr key={row.id}>
                <td className="px-3 py-2">{new Date(row.performed_at).toLocaleString()}<div className="text-xs text-gray-500">{row.performed_by}</div></td>
                <td className="px-3 py-2">
                  <PatronIdentityCell row={row} />
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    row.reason === 'PAYOUT' ? 'bg-green-100 text-green-800' :
                    row.reason === 'THRESHOLD' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {row.reason}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {row.payout_amount ? (
                    <span className="font-semibold text-green-600">
                      ${row.payout_amount.toLocaleString()}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-3 py-2">
                  {row.aml_check_id != null ? (
                    <div className="flex flex-wrap gap-1">
                      {(row.pep_match === true || row.pep_match === 1) && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800" title="PEP match">PEP</span>
                      )}
                      {(row.sanctions_match === true || row.sanctions_match === 1) && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800" title="Sanctions match">Sanctions</span>
                      )}
                      {(!row.pep_match || row.pep_match === 0) && (!row.sanctions_match || row.sanctions_match === 0) && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Clear</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2 max-w-xs">
                  <div className="truncate" title={row.notes}>
                    {row.notes || '—'}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className={`text-xs px-2 py-1 rounded ${row.attachments?.length ? 'bg-gray-100' : 'bg-gray-50 text-gray-500'}`}>
                      {row.attachments?.length
                        ? `${row.attachments.length} file${row.attachments.length > 1 ? 's' : ''}`
                        : 'No files'}
                    </span>
                    <button type="button" className="btn-secondary text-xs" onClick={() => openAttachments(row.id)}>View</button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length===0 && (
              <tr><td className="px-3 py-4 text-gray-500" colSpan={7}>No entries</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" onClick={()=>setOpen(false)}>
          <div className="w-full max-w-xl bg-white rounded shadow-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="text-lg font-semibold">New KYC Entry</div>
              <button className="btn-secondary" onClick={()=>setOpen(false)}>Close</button>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Member #</label>
                <input className="input-field w-full" value={form.member_id} onChange={(e)=>setForm(p=>({...p, member_id:e.target.value}))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Member Name</label>
                <input className="input-field w-full" value={form.member_name} onChange={(e)=>setForm(p=>({...p, member_name:e.target.value}))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Reason</label>
                <select className="select-field w-full" value={form.reason} onChange={(e)=>setForm(p=>({...p, reason:e.target.value}))}>
                  <option value="PAYOUT">Payout</option>
                  <option value="THRESHOLD">Threshold</option>
                  <option value="MANUAL">Manual</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Venue</label>
                <select className="select-field w-full" value={form.venue_id} onChange={(e)=>setForm(p=>({...p, venue_id:e.target.value}))}>
                  <option value="">— Select Venue —</option>
                  {venues.map(v => (
                    <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Payout Amount ($)</label>
                <input 
                  type="number" 
                  className="input-field w-full" 
                  value={form.payout_amount} 
                  onChange={(e)=>setForm(p=>({...p, payout_amount:e.target.value}))}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  onBlur={(e) => {
                    // Ensure the value is properly formatted when user leaves the field
                    const value = parseFloat(e.target.value)
                    if (!isNaN(value)) {
                      setForm(p=>({...p, payout_amount: value.toString()}))
                    }
                  }}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Related Case ID</label>
                <input className="input-field w-full" value={form.related_case_id} onChange={(e)=>setForm(p=>({...p, related_case_id:e.target.value}))} />
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">Notes</label>
                <textarea className="input-field w-full" rows={3} value={form.notes} onChange={(e)=>setForm(p=>({...p, notes:e.target.value}))} />
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">Attachments</label>
                <input 
                  type="file" 
                  ref={newEntryFileRef}
                  multiple
                  className="input-field w-full" 
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleNewEntryFileChange}
                />
                {uploadFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 mb-2">Selected files:</p>
                    <div className="space-y-1">
                      {uploadFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                          <span className="truncate">{file.name}</span>
                          <button 
                            type="button"
                            onClick={() => removeUploadFile(index)}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button className="btn-secondary" onClick={()=>{
                setOpen(false)
                setForm({ member_id:'', member_name:'', reason:'PAYOUT', related_case_id:'', notes:'', payout_amount:'' })
                setUploadFiles([])
                if (newEntryFileRef.current) newEntryFileRef.current.value = ''
              }}>Cancel</button>
              <button className="btn-primary" onClick={create}>
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {attachFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" onClick={()=>{
          setAttachFor(null)
          setExistingUploadFiles([])
          // Clean up image previews for this KYC entry
          const kycPreviews = Object.keys(imagePreviews).filter(key => key.startsWith(`${attachFor}_`))
          kycPreviews.forEach(key => {
            if (imagePreviews[key]) {
              URL.revokeObjectURL(imagePreviews[key])
            }
          })
          setImagePreviews(prev => {
            const newPreviews = { ...prev }
            kycPreviews.forEach(key => delete newPreviews[key])
            return newPreviews
          })
          if (fileRef.current) fileRef.current.value = ''
        }}>
          <div className="w-full max-w-4xl bg-white rounded shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="text-lg font-semibold">KYC record — {items.find(i => i.id === attachFor)?.member_id}</div>
              <button className="btn-secondary" onClick={()=>{
                setAttachFor(null)
                setExistingUploadFiles([])
                // Clean up image previews for this KYC entry
                const kycPreviews = Object.keys(imagePreviews).filter(key => key.startsWith(`${attachFor}_`))
                kycPreviews.forEach(key => {
                  if (imagePreviews[key]) {
                    URL.revokeObjectURL(imagePreviews[key])
                  }
                })
                setImagePreviews(prev => {
                  const newPreviews = { ...prev }
                  kycPreviews.forEach(key => delete newPreviews[key])
                  return newPreviews
                })
                if (fileRef.current) fileRef.current.value = ''
              }}>Close</button>
            </div>
            <div className="p-4">
              {(() => {
                const item = items.find(i => i.id === attachFor)
                if (!item) return null
                const venueLabel = (venues.find(v => String(v.id) === String(item.venue_id))?.name) || item.venue_id || '—'
                return (
                  <>
                    <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50/80 p-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Record details</h4>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div>
                          <dt className="text-xs text-gray-500">Performed</dt>
                          <dd className="font-medium">{item.performed_at ? new Date(item.performed_at).toLocaleString() : '—'}</dd>
                          <dd className="text-xs text-gray-600">{item.performed_by || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Patron</dt>
                          <dd className="mt-1">
                            <PatronIdentityCell row={item} />
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Reason</dt>
                          <dd>{item.reason || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Venue</dt>
                          <dd>{venueLabel}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Payout amount</dt>
                          <dd>{item.payout_amount != null && item.payout_amount !== '' ? `$${Number(item.payout_amount).toLocaleString()}` : '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Related case</dt>
                          <dd className="font-mono">{item.related_case_id || '—'}</dd>
                        </div>
                        <div className="sm:col-span-2">
                          <dt className="text-xs text-gray-500">AML / PEP</dt>
                          <dd>
                            {item.aml_check_id != null ? (
                              <span className="inline-flex flex-wrap gap-1">
                                {(item.pep_match === true || item.pep_match === 1) && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">PEP</span>
                                )}
                                {(item.sanctions_match === true || item.sanctions_match === 1) && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">Sanctions</span>
                                )}
                                {(!item.pep_match || item.pep_match === 0) && (!item.sanctions_match || item.sanctions_match === 0) && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Clear</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </dd>
                        </div>
                        <div className="sm:col-span-2">
                          <dt className="text-xs text-gray-500 mb-1">Notes</dt>
                          <dd className="whitespace-pre-wrap text-gray-800 bg-white border border-gray-100 rounded px-3 py-2 min-h-[2.5rem]">{item.notes?.trim() ? item.notes : '—'}</dd>
                        </div>
                      </dl>
                    </div>
                    <OcrExtractPanel ocrData={item.ocr_data} />
                  </>
                )
              })()}
              {/* Show existing attachments */}
              {(() => {
                const item = items.find(i => i.id === attachFor)
                return item?.attachments && item.attachments.length > 0 ? (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Existing Files:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {item.attachments.map((filename, idx) => {
                        const isImage = filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i)
                        const previewKey = `${attachFor}_${filename}`
                        
                        return (
                          <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                  {filename.toLowerCase().includes('.pdf') ? '📄' : 
                                   isImage ? '🖼️' : '📎'}
                                </div>
                                <span className="text-sm truncate" title={filename}>{filename}</span>
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button 
                                  className="btn-secondary text-xs" 
                                  onClick={() => viewAttachment(attachFor, filename)}
                                >
                                  Open
                                </button>
                                <button 
                                  className="btn-secondary text-xs text-red-600" 
                                  onClick={() => deleteAttachment(attachFor, filename)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                            
                            {/* Image Preview */}
                            {isImage && (
                              <div className="mt-2">
                                <ImagePreview 
                                  kycId={attachFor} 
                                  filename={filename} 
                                  previewKey={previewKey}
                                  imagePreviews={imagePreviews}
                                  loadImagePreview={loadImagePreview}
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <hr className="my-4" />
                  </div>
                ) : (
                  <div className="mb-4 text-sm text-gray-500">No attachments yet — use the section below to upload files.</div>
                )
              })()}

              {/* Upload new file */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Add New File:</h4>
                <input 
                  type="file" 
                  ref={fileRef} 
                  multiple 
                  className="input-field w-full" 
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleExistingUploadFileChange}
                />
                {existingUploadFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 mb-2">Selected files:</p>
                    <div className="space-y-1">
                      {existingUploadFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                          <span className="truncate">{file.name}</span>
                          <button 
                            type="button"
                            onClick={() => removeExistingUploadFile(index)}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              <div className="mt-3 flex items-center justify-end">
                  <button className="btn-primary" onClick={()=>upload(attachFor)}>
                    Upload {existingUploadFiles.length > 0 ? `${existingUploadFiles.length} File${existingUploadFiles.length > 1 ? 's' : ''}` : 'Files'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ScreenHelpPanel
        screenKey={KYC_REGISTRY_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="kyc-registry-help"
      />
    </div>
  )
}


